import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import type {
    OutboundConnectorCreateRequest,
    OutboundConnectorUpdateRequest,
    ScriptOutboundConfigCreateRequest,
    ScriptOutboundConfigUpdateRequest,
    ScriptValidateRequest,
} from '@/types/outbound-connector'
import {useToast} from '@/hooks/use-toast'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'


const QUERY_KEY = 'connectors'
const DETAIL_KEY = 'connector'
const LOGS_KEY = 'connector-logs'
const PROVISIONING_ACTIVE_KEY = 'outbound-connector-provisioning-active'
const PROVISIONING_HISTORY_KEY = 'outbound-connector-provisioning-history'


export function useOutboundConnectors(queryParams: string) {
    const services = useServices()
    return useQuery({
        queryKey: [QUERY_KEY, queryParams],
        queryFn: () => services.outboundConnectors.list(Object.fromEntries(new URLSearchParams(queryParams))),
    })
}

export function useOutboundConnector(id: string | undefined) {
    const services = useServices()
    return useQuery({
        queryKey: [DETAIL_KEY, id],
        queryFn: () => services.outboundConnectors.detail(id!),
        enabled: !!id,
    })
}

export function useOutboundConnectorLogs(id: string | undefined) {
    const services = useServices()
    return useQuery({
        queryKey: [LOGS_KEY, id],
        queryFn: () => services.outboundConnectors.logs(id!, {size: 20}),
        enabled: !!id,
    })
}

export function useCreateConnector() {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: OutboundConnectorCreateRequest) =>
            services.outboundConnectors.create(data),
        onSuccess: () => {
            toast({description: i18n.connectors.connectorCreated})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]})
        },
    })
}

export function useUpdateConnector(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: OutboundConnectorUpdateRequest) =>
            services.outboundConnectors.update(id, data),
        onSuccess: () => {
            toast({description: i18n.connectors.connectorToggled})
            queryClient.invalidateQueries({queryKey: [DETAIL_KEY, id]})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]})
        },
    })
}

export function useDeleteConnector() {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (id: string) =>
            services.outboundConnectors.remove(id),
        onSuccess: () => {
            toast({description: i18n.connectors.connectorDeleted})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]})
        },
    })
}

export function useTestConnector(id: string) {
    const services = useServices()
    return useMutation({
        mutationFn: async () => {
            const result = await services.outboundConnectors.test(id)
            if (!result.success) {
                throw new Error(result.message)
            }
            return result
        },
    })
}

export function useCreateScriptOutboundConfig(connectorId: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: ScriptOutboundConfigCreateRequest) =>
            services.outboundConnectors.createScriptConfig(connectorId, data),
        onSuccess: () => {
            toast({description: i18n.connectors.configurationSaved})
            queryClient.invalidateQueries({queryKey: [DETAIL_KEY, connectorId]})
        },
    })
}

export function useUpdateScriptOutboundConfig(connectorId: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: ScriptOutboundConfigUpdateRequest) =>
            services.outboundConnectors.updateScriptConfig(connectorId, data),
        onSuccess: () => {
            toast({description: i18n.connectors.configurationSaved})
            queryClient.invalidateQueries({queryKey: [DETAIL_KEY, connectorId]})
        },
    })
}


export function useValidateOutboundScript(connectorId: string) {
    const services = useServices()

    return useMutation({
        mutationFn: (data: ScriptValidateRequest) =>
            services.outboundConnectors.validateScript(connectorId, data),
    })
}

export function useOutboundConnectorProvisioningTasks(connectorId: string | undefined, queryParams: string = '') {
    const services = useServices()
    return useQuery({
        queryKey: [PROVISIONING_ACTIVE_KEY, connectorId, queryParams],
        queryFn: () => services.outboundConnectors.provisioningTasks(
            connectorId!,
            Object.fromEntries(new URLSearchParams(queryParams)),
        ),
        enabled: !!connectorId,
    })
}

export function useOutboundConnectorProvisioningHistory(connectorId: string | undefined, queryParams: string = '') {
    const services = useServices()
    return useQuery({
        queryKey: [PROVISIONING_HISTORY_KEY, connectorId, queryParams],
        queryFn: () => services.outboundConnectors.provisioningHistory(
            connectorId!,
            Object.fromEntries(new URLSearchParams(queryParams)),
        ),
        enabled: !!connectorId,
    })
}
